import React, { useCallback } from "react";
import { Container, Col, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import withStyles from "isomorphic-style-loader/withStyles";
import styles from "./styles.scss";
import { CHARITY_CUSTOMIZE_HUB_PATH } from "../../../../../../utils/onboarding/stepNavigation";
import { BrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import ICharityEmailSetup from "../../../../../../types/ICharityEmailSetup";

interface OrganizationSetupNode {
  lastSaved?: number;
  completedAt?: number;
}

interface CustomizeHubCard {
  key: "organization" | "email" | "branding";
  title: string;
  description: string;
  route: string;
  lastSavedAt?: number;
  lastSavedLoaded: boolean;
}

interface Props {
  charityId: string;
  charityName: string;
  organizationSetup: OrganizationSetupNode | null;
  organizationSetupLoaded: boolean;
  emailSetup: ICharityEmailSetup | null;
  emailSetupLoaded: boolean;
  brandSetup: BrandSetupData | null;
  brandSetupLoaded: boolean;
}

const formatLastUpdated = (loaded: boolean, timestamp?: number): string => {
  if (!loaded) {
    return "";
  }
  if (!timestamp) {
    return "Never updated";
  }
  const date = new Date(timestamp);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hour12 = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const meridiem = date.getHours() >= 12 ? "PM" : "AM";
  return `Last updated ${month}/${day}/${year} ${hour12}:${minutes} ${meridiem}`;
};

const websiteLastSaved = (brandSetup: BrandSetupData | null): number | undefined => {
  if (!brandSetup) {
    return undefined;
  }
  const stamps = [
    brandSetup.updatedAt,
    brandSetup.publishedAt,
    brandSetup.newsfeedLastSaved,
    brandSetup.givingLastSaved,
  ].filter((value): value is number => typeof value === "number" && value > 0);
  if (stamps.length === 0) {
    return undefined;
  }
  return Math.max(...stamps);
};

const CustomizeHubPage: React.FC<Props> = ({
  charityId,
  charityName,
  organizationSetup,
  organizationSetupLoaded,
  emailSetup,
  emailSetupLoaded,
  brandSetup,
  brandSetupLoaded,
}) => {
  const navigate = useNavigate();

  const handleTaskClick = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  const cards: CustomizeHubCard[] = [
    {
      key: "organization",
      title: "Organization Data",
      description: "Legal name, address, and contact details",
      route: `${CHARITY_CUSTOMIZE_HUB_PATH}/organization`,
      lastSavedAt: organizationSetup?.completedAt ?? organizationSetup?.lastSaved,
      lastSavedLoaded: organizationSetupLoaded,
    },
    {
      key: "email",
      title: "Sending addresses",
      description: "From-addresses used to email contacts",
      route: `${CHARITY_CUSTOMIZE_HUB_PATH}/sending-emails`,
      lastSavedAt: emailSetup?.lastSaved,
      lastSavedLoaded: emailSetupLoaded,
    },
    {
      key: "branding",
      title: "Customize Website",
      description: "Giving site, newsfeed, and post pages",
      route: `${CHARITY_CUSTOMIZE_HUB_PATH}/website`,
      lastSavedAt: websiteLastSaved(brandSetup),
      lastSavedLoaded: brandSetupLoaded,
    },
  ];

  return (
    <Container key={charityId} className={styles.root}>
      <Row className={styles.mainContainer}>
        <Col xs={12}>
          <div className={styles.dashboardSections}>
            <section className={styles.setupSectionPanel}>
              <div className={styles.sectionHeader}>
                <h6 className={styles.sectionTitle}>
                  Customize {charityName || "charity"}
                </h6>
              </div>
              <div className={styles.cardsStrip}>
                {cards.map(card => (
                  <div key={card.key} className={styles.cardSlot}>
                    <div
                      className={styles.taskItem}
                      onClick={() => handleTaskClick(card.route)}
                    >
                      <h6 className={styles.cardTitle}>{card.title}</h6>
                      <div
                        className={`${styles.titleRule} ${
                          card.key === "organization"
                            ? styles.titleRule_organization
                            : card.key === "email"
                              ? styles.titleRule_email
                              : styles.titleRule_branding
                        }`}
                      />
                      <p className={styles.cardDescription}>{card.description}</p>
                      <p className={styles.cardLastSaved}>
                        {formatLastUpdated(card.lastSavedLoaded, card.lastSavedAt)}
                      </p>
                      <div className={styles.cardFooter}>
                        <span className={styles.editLabel}>Edit</span>
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state: any) => {
  const charityId = state.user?.currentCharity?.id || "";
  const { firebase } = state;

  const organizationSetupLoaded = charityId
    ? isLoaded(get(firebase, `data.${charityId}.charitySetup.organization`))
    : true;
  const emailSetupLoaded = charityId
    ? isLoaded(get(firebase, `data.${charityId}.charitySetup.email`))
    : true;
  const brandSetupLoaded = charityId
    ? isLoaded(get(firebase, `data.${charityId}.charitySetup.brandSetup`))
    : true;

  return {
    charityId,
    charityName: state.user?.currentCharity?.name || "",
    organizationSetup: organizationSetupLoaded
      ? get(firebase, `data.${charityId}.charitySetup.organization`, null)
      : null,
    organizationSetupLoaded,
    emailSetup: emailSetupLoaded
      ? get(firebase, `data.${charityId}.charitySetup.email`, null)
      : null,
    emailSetupLoaded,
    brandSetup: brandSetupLoaded
      ? get(firebase, `data.${charityId}.charitySetup.brandSetup`, null)
      : null,
    brandSetupLoaded,
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((props: any) => {
    const charityId = props.charityId;
    if (!charityId) {
      return [];
    }
    return [
      `${charityId}/charitySetup/organization`,
      `${charityId}/charitySetup/email`,
      `${charityId}/charitySetup/brandSetup`,
    ];
  })(withStyles(styles)(CustomizeHubPage))
);
